// React Imports
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

// MUI Imports
import { Box, Button, Chip, MenuItem } from '@mui/material'

// Utils Imports
import { pageSizeOptions } from '../../../utils/constant'
import { downloadPDFBlob, showErrorToast } from '../../../utils/utils'

// API Imports
import {
  exportMonthlyUsageReport,
  getMonthlyUsageReport
} from '../../../axios/api/subscription'

// Components Imports
import CustomDataGrid from '../../../components/data-grid'
import Icon from '../../../components/icon'
import OverlayLoading from '../../../components/overlay-loading'
import CustomTextField from '../../../components/textfield'
import VirtualizedAutocomplete from '../../../components/virtualized-autocomplete'

// Default Filters
const currentDate = new Date()
const MONTHS = [
  'JANUARY',
  'FEBRUARY',
  'MARCH',
  'APRIL',
  'MAY',
  'JUNE',
  'JULY',
  'AUGUST',
  'SEPTEMBER',
  'OCTOBER',
  'NOVEMBER',
  'DECEMBER'
]

// Generate last 10 years (including current)
const currentYear = currentDate.getFullYear()
const YEAR_OPTIONS = Array.from({ length: 10 }, (_, i) => currentYear - i)

const DEFAULT_FILTERS = {
  page: 0,
  size: pageSizeOptions?.[0],
  sortBy: 'totalAttempts',
  sortOrder: 'DESC',
  month: [MONTHS[currentDate.getMonth()]],
  year: currentYear
}

// Columns
const columns = [
  {
    flex: 0.2,
    minWidth: 200,
    display: 'flex',
    field: 'contestName',
    headerName: 'Contest Name'
  },
  {
    flex: 0.2,
    minWidth: 200,
    display: 'flex',
    field: 'totalCandidates',
    headerName: 'Total Candidates',
    renderCell: ({ row }) => <>{row?.totalCandidates || 0}</>
  },
  {
    flex: 0.2,
    minWidth: 200,
    display: 'flex',
    field: 'totalAttempts',
    headerName: 'Total Attempts',
    renderCell: ({ row }) => (
      <Box sx={{ gap: '4px', display: 'flex', alignItems: 'center' }}>
        {row?.totalAttempts || 0}
        {row?.reEnableCount > 0 && (
          <Icon
            title={`Re-enabled ${row?.reEnableCount} times`}
            icon={'material-symbols:info-outline'}
            fontSize={'16px'}
          />
        )}
      </Box>
    )
  },
  {
    flex: 0.2,
    minWidth: 250,
    display: 'flex',
    field: 'completedLastStageAttempts',
    headerName: 'Candidates Completed All Stages',
    renderCell: ({ row }) => <>{row?.completedLastStageAttempts || 0}</>
  },
  {
    flex: 0.2,
    minWidth: 200,
    display: 'flex',
    field: 'totalCredits',
    headerName: 'Total Credits Consumed',
    renderCell: ({ row }) => <>{row?.totalCredits || 0}</>
  }
]

// Always show max 2 chips + +N, even while dropdown is open
const renderMonthTags = (value, getTagProps) => {
  const visible = value.slice(0, 2)
  const hiddenCount = value.length - visible.length
  return (
    <>
      {visible.map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index })
        return <Chip key={key} label={option} size="small" {...tagProps} />
      })}
      {hiddenCount > 0 && <Chip label={`+${hiddenCount}`} size="small" />}
    </>
  )
}

const MonthlyReportTab = ({ inputMonths, inputYear }) => {
  // State
  const [data, setData] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [filters, setFilters] = useState({
    ...DEFAULT_FILTERS,
    ...(inputMonths ? { month: inputMonths } : {}),
    ...(inputYear ? { year: inputYear } : {})
  })
  const [loading, setLoading] = useState({ fetch: true, download: false })

  // Variable
  const pageCount = useMemo(() => {
    return Math.ceil((totalCount || 0) / filters?.size)
  }, [filters?.size, totalCount])

  const memoizedColumns = useMemo(() => {
    return columns
  }, [])

  // Handlers
  const fetchMonthlyReport = useCallback(async (filters) => {
    setLoading((prev) => ({ ...prev, fetch: true }))
    const response = await getMonthlyUsageReport(filters)
    setLoading((prev) => ({ ...prev, fetch: false }))

    if (response?.isError) {
      setData([])
      setTotalCount(0)
      showErrorToast(response)
    } else {
      setData(response?.content || [])
      setTotalCount(response?.totalCount || 0)
    }
  }, [])

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => {
      return {
        ...prev,
        [key === 'pageSize' ? 'size' : key]: value,
        ...(key !== 'page' ? { page: 0 } : {})
      }
    })
  }, [])

  const handleSortModelChange = useCallback((sortModel) => {
    const field = sortModel?.[0]?.field
    const direction = sortModel?.[0]?.sort?.toUpperCase()

    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder: direction
    }))
  }, [])

  const handleMonthChange = useCallback((_, values) => {
    setFilters((prev) => ({ ...prev, month: values, page: 0 }))
  }, [])

  const handleYearChange = useCallback((e) => {
    setFilters((prev) => ({ ...prev, year: e.target.value, page: 0 }))
  }, [])

  const handleExport = useCallback(async () => {
    setLoading((prev) => ({ ...prev, download: true }))
    const response = await exportMonthlyUsageReport({
      sortBy: filters?.sortBy,
      sortOrder: filters?.sortOrder,
      month: filters?.month,
      year: filters?.year
    })
    setLoading((prev) => ({ ...prev, download: false }))
    if (response?.isError) {
      showErrorToast(response)
    } else {
      downloadPDFBlob(response, 'monthly-usage-report.csv', {
        type: 'text/csv'
      })
    }
  }, [filters])

  //useEffect
  useEffect(() => {
    fetchMonthlyReport(filters)
  }, [filters, fetchMonthlyReport])

  return (
    <>
      {(loading?.fetch || loading?.download) && <OverlayLoading />}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: '15px',
          gap: '12px'
        }}
      >
        <Button
          variant="contained"
          startIcon={<Icon icon={'ix:download'} style={{ fontSize: '18px' }} />}
          onClick={handleExport}
          sx={{ padding: '10px 19px' }}
        >
          Export CSV
        </Button>

        <Box sx={{ minWidth: '150px' }}>
          <VirtualizedAutocomplete
            autoCompleteProps={{
              options: MONTHS,
              multiple: true,
              value: filters?.month || [],
              onChange: handleMonthChange,
              disableCloseOnSelect: true,
              placeholder: 'Select Month(s)',
              size: 'small',
              renderTags: renderMonthTags,
              sx: {
                '& .MuiAutocomplete-inputRoot': {
                  flexWrap: 'nowrap',
                  overflow: 'hidden'
                }
              }
            }}
            needVirtualization={false}
          />
        </Box>

        <CustomTextField
          select
          size="small"
          value={filters?.year}
          onChange={handleYearChange}
          SelectProps={{
            value: filters?.year,
            onChange: handleYearChange
          }}
        >
          {YEAR_OPTIONS.map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </CustomTextField>
      </Box>
      <CustomDataGrid
        rows={data}
        columns={memoizedColumns}
        getRowId={(row) => {
          return row?.contestId
        }}
        pageCount={pageCount}
        pageSizeValue={filters?.size}
        pageValue={filters?.page}
        handlePaginationChange={handleFilterChange}
        rowCount={totalCount}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        disableColumnResize={true}
        sortModel={[
          {
            field: filters?.sortBy,
            sort: filters?.sortOrder?.toLowerCase()
          }
        ]}
        onSortModelChange={handleSortModelChange}
        rowHeight={52}
      />
    </>
  )
}

export default memo(MonthlyReportTab)
